import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft, Download, Copy, Share2, Sparkles, Eraser, Layers, Check, Lock, Wand2, Shuffle, Play, Pause, X, ChevronDown, Menu,
} from "lucide-react";
import { toPng } from "html-to-image";
import { MoodCard } from "../components/MoodCard";
import ExportCard from "../components/ExportCard";
import { ShareSheet } from "../components/ShareSheet";
import ShareAfter from "../components/ShareAfter";
import PremiumModal from "../components/PremiumModal";
import PremiumPackTeaser from "../components/PremiumPackTeaser";
import VersionStepper from "../components/VersionStepper";
import AnimatedExport from "../components/AnimatedExport";
import { Toaster } from "../components/ui/sonner";
import { getMoodCard, selectStyle, mintShareSlug, validateExport, API, regenerateMoodCard, switchPack } from "../lib/api";
import axios from "axios";
import { updateHistoryStyle } from "../lib/history";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";
import { buildShareCaption, buildShareCaptionText } from "../lib/shareCaption";
import { canonicalShareUrl } from "../lib/shareUrl";
import { buildPrivateShareMessage } from "../lib/privateShareMessage";
import TourHint from "../components/TourHint";
import RestyleDrawer from "../components/RestyleDrawer";
import DownloadOverlay from "../components/DownloadOverlay";
import PackIterationStrip from "../components/PackIterationStrip";
import { resolveVariant } from "../lib/cardVariants";
import { isStylePackV2, resolveRecipe, pickShuffleRecipe, setRecipeOverride } from "../lib/stylePacks";
import { tap, double } from "../lib/haptic";

// iter-73: new mobile-first studio components. Legacy dense UI is wrapped
// in `hidden lg:block` so desktop stays untouched while mobile gets the
// clean Remix Studio experience.
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";
import MobileStyleRail from "../components/mobile/MobileStyleRail";
import MobileActionBar from "../components/mobile/MobileActionBar";
import RemixStudio from "../components/mobile/RemixStudio";
import DownloadSheet from "../components/mobile/DownloadSheet";
import AnimateTeaser from "../components/mobile/AnimateTeaser";
import CreatorReactionStrip from "../components/CreatorReactionStrip";
import CrisisHelpFooter from "../components/CrisisHelpFooter";

// iter-73 follow-up: persist pack swaps to server so they survive reload

const PLATFORM_SIZES = [
    { key: "story", label: "Story 9:16",   width: 1080, height: 1920 },
    { key: "feed",  label: "Feed 1:1",     width: 1080, height: 1080 },
    { key: "port",  label: "Portrait 4:5", width: 1080, height: 1350 },
    { key: "x",     label: "X 16:9",       width: 1200, height: 675  },
];

export default function CardDetail() {
    const { id, style } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const exportRefStory = useRef(null);
    const exportRefFeed = useRef(null);
    const exportRefPort = useRef(null);
    const exportRefX = useRef(null);

    const [mood, setMood] = useState(location.state?.mood || null);
    const [copied, setCopied] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    // Restyle (variant swap) — Premium feature gate. The resolved variant
    // applies on render; the drawer lets the user swap if they're paid.
    // We bump a tick to force re-resolve from localStorage when the
    // drawer writes a new override.
    const [restyleOpen, setRestyleOpen] = useState(false);
    const [variantTick, setVariantTick] = useState(0);
    const [shareAfterOpen, setShareAfterOpen] = useState(false);
    const [premiumOpen, setPremiumOpen] = useState(false);
    // iter-72 (b): friendly intermediate teaser when a free user taps a locked
    // Style Pack in the iteration strip. Holds the packId so the teaser can
    // name + preview the pack the user actually tapped.
    const [teaserPackId, setTeaserPackId] = useState(null);
    const [animateOpen, setAnimateOpen] = useState(false);
    // iter-73 mobile studio sheets
    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    const [remixOpen, setRemixOpen] = useState(false);
    const [downloadSheetOpen, setDownloadSheetOpen] = useState(false);
    const [animateTeaserOpen, setAnimateTeaserOpen] = useState(false);
    // In-place motion treatment toggle — applies the recipe's animation
    // (drift / breathe / shimmer / glow) to the live preview. Premium-only.
    // localStorage'd per card so it survives navigation.
    const [motionOn, setMotionOn] = useState(() => {
        try {
            return window.localStorage.getItem(`moodcard.motion.${id}`) === "1";
        } catch {
            return false;
        }
    });
    const [exporting, setExporting] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [shareLinks, setShareLinks] = useState(null); // { share_url, remix_url, slug }
    // iter-81: every consumer goes through canonicalShareUrl() so even if
    // a backend env var ever drifts (APP_PUBLIC_URL vs PUBLIC_DOMAIN), the
    // shared URL is always moodcard.app — never the preview hostname.
    const canonShareUrl = shareLinks?.share_url
        ? canonicalShareUrl(shareLinks.share_url)
        : `https://moodcard.app/c/${shareLinks?.slug || id}`;
    const [serverWatermarkRequired, setServerWatermarkRequired] = useState(null);
    // Share-back virality — when on, downloads bake a small "scan to remix"
    // QR code into the bottom-right corner. Persisted across reloads.
    const [withQr, setWithQr] = useState(() => {
        try { return localStorage.getItem("moodcard.qr") === "1"; } catch { return false; }
    });

    // Reply context — seeded from Home's sessionStorage when this card is
    // a reply to a sender whose first name we know. Pre-fills the
    // ShareSheet recipient field so the user doesn't retype.
    const replyToDefault = (() => {
        try { return window.sessionStorage.getItem("moodcard.reply_to") || ""; } catch { return ""; }
    })();

    const { isPremium, isAdmin } = useAuth();

    useEffect(() => {
        if (mood) return;
        (async () => {
            try {
                const data = await getMoodCard(id);
                setMood(data);
            } catch (e) {
                navigate("/", { replace: true });
            }
        })();
    }, [id, mood, navigate]);

    useEffect(() => {
        if (mood) {
            selectStyle(id, style).catch(() => {});
            updateHistoryStyle(id, style);
            track("card_detail_view", { id, style, pack: mood.pack });
            // Mint share slug (idempotent) so a public moodcard.app URL exists.
            mintShareSlug(id).then(setShareLinks).catch(() => {});
            // Server-side validation: should this user's export show the watermark?
            validateExport(id)
                .then((r) => setServerWatermarkRequired(!!r.watermark_required))
                .catch(() => setServerWatermarkRequired(true)); // fail-safe: assume Free
        }
    }, [mood, id, style]);

    if (!mood) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-app">
                <span className="font-sans text-xs uppercase tracking-[0.3em] text-app-3">loading…</span>
            </div>
        );
    }

    const card = mood.cards.find((c) => c.style === style) || mood.cards[0];
    // Watermark visibility is the AND of (server-side plan check) and (client flag).
    // Default to TRUE (Free) until the server responds — prevents flash-of-no-watermark cheat.
    const showWatermark = serverWatermarkRequired !== false || !isPremium;

    const exportNode = async (node) => {
        if (!node) return null;
        return await toPng(node, {
            cacheBust: true,
            pixelRatio: 2,
            backgroundColor: "#050507",
            skipFonts: true,
        });
    };

    // Server-rendered PNG fetch. Pixel-identical to the in-app preview because
    // it's the in-app preview, rendered by a headless Chromium on the backend.
    // Returns a Blob → object URL we can hand to the download trigger.
    // Aspect names match the backend ASPECT_SIZES keys exactly.
    const fetchServerPng = async (aspect = "story", withQr = false) => {
        const qrParam = withQr ? "&qr=1" : "";
        // Honour the user's swap — premium-applied variants get baked into
        // the downloaded PNG. variantTick is unread here but referenced so
        // ESLint understands the dependency; resolved variant is read live.
        // eslint-disable-next-line no-unused-vars
        const _ = variantTick;
        const packId = mood?.pack || card?.pack;
        let recipeParam = "";
        let variantParam = "";
        if (isStylePackV2(packId)) {
            const rcp = resolveRecipe({
                moodId: id,
                pack: packId,
                recipeId: card?.recipe_id,
                customBackgroundUrl: mood?.custom_background_url || card?.custom_background_url,
            });
            if (rcp?.key) recipeParam = `&recipe=${encodeURIComponent(rcp.key)}`;
        } else {
            const v = resolveVariant(id);
            if (v && v.id !== "default") variantParam = `&variant=${v.id}`;
        }
        const url = `${window.location.origin}/api/cards/${id}/export.png?style=${encodeURIComponent(card.style)}&aspect=${encodeURIComponent(aspect)}${qrParam}${variantParam}${recipeParam}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`server render failed: ${res.status}`);
        const blob = await res.blob();
        return URL.createObjectURL(blob);
    };

    const triggerDownload = (dataUrl, filename) => {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleDownload = async () => {
        setExporting(true);
        try {
            // Server-side render (Playwright/Chromium) — pixel-identical to the in-app preview.
            // Falls back to legacy html-to-image only if the server route is unreachable.
            let dataUrl;
            try {
                dataUrl = await fetchServerPng("story", withQr);
            } catch (serverErr) {
                console.warn("server render failed, falling back to client export", serverErr);
                dataUrl = await exportNode(exportRefStory.current);
            }
            if (!dataUrl) return null;
            triggerDownload(dataUrl, `moodcard-${card.style}-${id.slice(0, 6)}.png`);
            setDownloaded(true);
            double();  // double-pulse haptic on confirmed success
            track("card_downloaded", { id, style: card.style, pack: mood.pack });
            setTimeout(() => setDownloaded(false), 1800);
            // Show viral prompt 600ms after download completes (non-blocking).
            setTimeout(() => setShareAfterOpen(true), 600);
            return dataUrl;
        } catch (e) {
            console.error("export failed", e);
        } finally {
            setExporting(false);
        }
        return null;
    };

    const handleAllPlatforms = async () => {
        if (!isPremium) { setPremiumOpen(true); return; }
        setExporting(true);
        try {
            for (let i = 0; i < PLATFORM_SIZES.length; i++) {
                const s = PLATFORM_SIZES[i];
                let url;
                try {
                    url = await fetchServerPng(s.key, withQr);
                } catch (e) {
                    // Per-aspect fallback to legacy client export.
                    const refs = [exportRefStory, exportRefFeed, exportRefPort, exportRefX];
                    url = await exportNode(refs[i].current);
                }
                if (url) triggerDownload(url, `moodcard-${card.style}-${s.key}.png`);
                await new Promise((r) => setTimeout(r, 250));
            }
            toast.success("All platform sizes exported");
        } catch (e) {
            console.error(e);
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    /**
     * Export ONE platform size. Free users still get 9:16 Story via the main
     * Download button — single-size selector is a Plus+ convenience.
     */
    const handleExportPlatform = async (key) => {
        if (!isPremium) { setPremiumOpen(true); return; }
        const refMap = {
            story: exportRefStory,
            feed:  exportRefFeed,
            port:  exportRefPort,
            x:     exportRefX,
        };
        const ref = refMap[key];
        const meta = PLATFORM_SIZES.find((p) => p.key === key);
        if (!meta) return;
        setExporting(true);
        try {
            let url;
            try {
                url = await fetchServerPng(meta.key, withQr);
            } catch (e) {
                console.warn("server render failed, falling back to client export", e);
                url = ref?.current ? await exportNode(ref.current) : null;
            }
            if (url) {
                triggerDownload(url, `moodcard-${card.style}-${meta.key}.png`);
                track("card_downloaded", { id, style: card.style, pack: mood.pack, platform: meta.key });
                toast.success(`Saved · ${meta.label}`);
                setTimeout(() => setShareAfterOpen(true), 500);
            }
        } catch (e) {
            console.error(e);
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const handleAnimate = () => {
        if (!isPremium) { setPremiumOpen(true); return; }
        setAnimateOpen(true);
    };

    const handleRemoveWatermark = () => {
        if (!isPremium) { setPremiumOpen(true); return; }
        toast.success("Watermark removed", {
            description: "Your next download will be pristine.",
        });
    };

    const handleCopyCaption = async () => {
        try {
            // Copy the FULL viral share caption (line + CTA + hashtags), not just the caption
            const text = buildShareCaption({ card, shareUrl: canonShareUrl });
            await navigator.clipboard.writeText(text);
            setCopied(true);
            track("copy_link_clicked", { source: "card_detail_copy_caption" });
            setTimeout(() => setCopied(false), 1800);
        } catch (e) { console.error(e); }
    };

    return (
        <div className="relative min-h-screen w-full bg-app overflow-x-hidden" data-testid="card-detail-screen">
            <div className="absolute inset-0 aurora-wash opacity-30 pointer-events-none" />
            <div className="absolute inset-0 grain pointer-events-none" />
            <Toaster theme="dark" position="bottom-center" />

            {/* iter-73: mobile top bar (hidden on lg+). Hamburger left,
                MoodCard wordmark center, bell right — nothing else. */}
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                onOpenNotifications={() => navigate("/me")}
                hasNotifications={false}
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />

            <header className="hidden lg:flex relative z-10 max-w-md lg:max-w-5xl mx-auto px-6 pt-6 items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-app-2 hover:text-app transition-colors"
                        data-testid="card-back-button"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                        <span className="font-sans text-xs uppercase tracking-[0.3em]">back</span>
                    </button>
                    {/* iter-81: desktop hamburger menu — same component / items as mobile */}
                    <span className="w-px h-4 bg-app/15" aria-hidden="true" />
                    <button
                        onClick={() => setHamburgerOpen(true)}
                        className="flex items-center justify-center h-8 w-8 text-app-2 hover:text-app transition-colors"
                        data-testid="card-desktop-menu"
                        aria-label="Open menu"
                        title="Menu"
                    >
                        <Menu className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </div>
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                    moodcard
                </span>
            </header>

            <div className="relative z-10 max-w-md lg:max-w-5xl mx-auto px-4 sm:px-6 pt-4 sm:pt-8 pb-16">
                <div className="lg:grid lg:grid-cols-[1fr_1fr] lg:gap-12 lg:items-start">
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="lg:sticky lg:top-6">
                    {/* Card preview — 240px on mobile (~427px tall, 9:16).
                        Fits the suggested-caption + action row above the fold
                        AND keeps the footer wordmark on one comfortable line.
                        Tap → lightbox modal at ~340px for the full read.
                        Desktop (lg+) caps at 380px. */}
                    <button
                        type="button"
                        onClick={() => setLightboxOpen(true)}
                        data-testid="card-preview-tap-to-enlarge"
                        aria-label="enlarge card preview"
                        className="block mx-auto w-full max-w-[240px] sm:max-w-[280px] lg:max-w-[380px] rounded-[18px] transition-transform hover:scale-[1.03] active:scale-[0.98]"
                    >
                    {(() => {
                        // Resolve variant on every render. variantTick is in the
                        // dependency chain so the drawer's localStorage write
                        // triggers a fresh resolution.
                        // eslint-disable-next-line no-unused-vars
                        const _ = variantTick;
                        const variant = resolveVariant(id);
                        return (
                            <MoodCard
                                card={card}
                                moodId={id}
                                showWatermark={showWatermark}
                                lane={mood.lane || mood.personalize?.lane}
                                temperature={mood.personalize?.temperature || mood.temperature}
                                variant={variant}
                                enableMotion={motionOn && isPremium}
                            />
                        );
                    })()}
                    </button>
                </motion.div>

                {/* Offscreen export nodes — pixel-locked ExportCard with auto-shrink.
                    aria-hidden so the duplicated 'MADE WITH MOODCARD.APP' text
                    is not read out by screen readers (4 copies at 1080-1200px
                    width were being announced as duplicate content). */}
                <div
                    aria-hidden="true"
                    style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }}
                >
                    <div ref={exportRefStory}>
                        <ExportCard card={card} width={1080} height={1920} showWatermark={showWatermark} lane={mood.lane || mood.personalize?.lane} temperature={mood.personalize?.temperature || mood.temperature} />
                    </div>
                    <div ref={exportRefFeed}>
                        <ExportCard card={card} width={1080} height={1080} showWatermark={showWatermark} lane={mood.lane || mood.personalize?.lane} temperature={mood.personalize?.temperature || mood.temperature} />
                    </div>
                    <div ref={exportRefPort}>
                        <ExportCard card={card} width={1080} height={1350} showWatermark={showWatermark} lane={mood.lane || mood.personalize?.lane} temperature={mood.personalize?.temperature || mood.temperature} />
                    </div>
                    <div ref={exportRefX}>
                        <ExportCard card={card} width={1200} height={675} showWatermark={showWatermark} lane={mood.lane || mood.personalize?.lane} temperature={mood.personalize?.temperature || mood.temperature} />
                    </div>
                </div>

                {/* RIGHT column on lg+ — caption + share actions. On mobile
                    this is just the natural flow below the card. */}
                <div className="lg:min-w-0">

                {/* caption — tap to copy. The block IS the action, no extra button.
                    iter-74: hidden on mobile here — re-mounted BELOW the style rail
                    so the visual flow is Card → Styles → Caption → Action bar (per
                    the design correction). Desktop (lg+) keeps it in the right column. */}
                <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
                    onClick={handleCopyCaption}
                    data-testid="card-caption-block"
                    className="hidden lg:block btn-press w-full text-left rounded-2xl border border-app/30 bg-surface-1 backdrop-blur-md p-4 sm:p-5 hover:border-app/55 hover:bg-surface-2 transition-colors group"
                >
                    <div className="flex items-center justify-between">
                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                            suggested caption
                        </span>
                        <span className="flex items-center gap-1 font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 group-hover:text-app transition-colors">
                            {copied ? <><Check className="w-3 h-3" strokeWidth={2.2} /> copied</> : <><Copy className="w-3 h-3" strokeWidth={1.6} /> tap to copy</>}
                        </span>
                    </div>
                    <p className="mt-3 font-serif italic text-app text-[14px] sm:text-base lg:text-lg leading-relaxed whitespace-pre-line">
                        {card.caption}
                    </p>
                </motion.button>

                {/* iter-78 mobile: collapsed reaction counts for the creator.
                    Hidden until the card has activity (felt/candle/opens > 0). */}
                <CreatorReactionStrip
                    slug={shareLinks?.slug || mood?.slug}
                    opens={mood?.opens_count || 0}
                />

                {/* iter-80: quiet crisis-aware helpline footer for the creator. */}
                <CrisisHelpFooter
                    show={!!mood?.crisis_flag}
                    locality={mood?.personalize?.locality}
                />

                {/* iter-73 mobile: clean style rail directly below the card.
                    Replaces the dense PackIterationStrip on mobile. */}
                <div className="lg:hidden mt-5">
                    {(() => {
                        // eslint-disable-next-line no-unused-vars
                        const _t = variantTick;
                        const activeRecipe = isStylePackV2(mood?.pack || card?.pack)
                            ? resolveRecipe({
                                moodId: id,
                                pack: mood?.pack || card?.pack,
                                recipeId: card?.recipe_id,
                                customBackgroundUrl: mood?.custom_background_url || card?.custom_background_url,
                            })
                            : null;
                        return (
                            <MobileStyleRail
                                moodId={id}
                                currentPack={activeRecipe?.pack?.id || mood?.pack || card?.pack}
                                isPremium={isPremium}
                                onChange={async (packId) => {
                                    setVariantTick((n) => n + 1);
                                    // iter-73 fix: persist pack swap to server so it
                                    // survives reload. Fire-and-forget; the live
                                    // recipe override has already updated visually.
                                    try {
                                        const updated = await switchPack(id, packId);
                                        setMood(updated);
                                    } catch (e) {
                                        if (e?.response?.status === 402) {
                                            setTeaserPackId(packId);
                                        }
                                        // Otherwise stay quiet — visual override still applies
                                    }
                                }}
                                onPremiumLocked={(packId) => setTeaserPackId(packId)}
                            />
                        );
                    })()}
                </div>

                {/* iter-74 mobile correction: caption sits BELOW the style rail
                    (between styles and action bar). On lg+ the original caption
                    block in the right column handles this; this duplicate is
                    mobile-only via lg:hidden. */}
                <motion.button
                    type="button"
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
                    onClick={handleCopyCaption}
                    data-testid="card-caption-block-mobile"
                    className="lg:hidden btn-press mt-5 w-full text-left rounded-2xl border border-app/30 bg-surface-1 backdrop-blur-md p-4 hover:border-app/55 hover:bg-surface-2 transition-colors group"
                >
                    <div className="flex items-center justify-between">
                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                            suggested caption
                        </span>
                        <span className="flex items-center gap-1 font-sans text-[9px] uppercase tracking-[0.32em] text-app-3 group-hover:text-app transition-colors">
                            {copied ? <><Check className="w-3 h-3" strokeWidth={2.2} /> copied</> : <><Copy className="w-3 h-3" strokeWidth={1.6} /> tap to copy</>}
                        </span>
                    </div>
                    <p className="mt-3 font-serif italic text-app text-[14px] leading-relaxed whitespace-pre-line">
                        {card.caption}
                    </p>
                </motion.button>

                {/* Legacy dense desktop UI — wrapped to render only on lg+. The
                    new mobile flow surfaces these affordances via MobileActionBar
                    + RemixStudio + DownloadSheet instead. */}
                <div className="hidden lg:block">

                {/* actions */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-5"
                >
                {/* Share-back QR toggle — free feature; makes every download a discovery loop */}
                    <button
                        type="button"
                        onClick={() => {
                            const next = !withQr;
                            setWithQr(next);
                            try { localStorage.setItem("moodcard.qr", next ? "1" : "0"); } catch { /* ignore */ }
                            track("qr_toggle", { on: next, id });
                        }}
                        data-testid="card-qr-toggle"
                        className={`mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[11px] font-sans tracking-wide transition-colors ${
                            withQr
                                ? "border-emerald-400/55 bg-emerald-400/10 text-emerald-100"
                                : "border-app/25 bg-surface-1 text-app-2 hover:text-app hover:border-app/45"
                        }`}
                    >
                        <span
                            className={`inline-block h-3 w-3 rounded-[2px] border-2 transition-colors ${withQr ? "border-emerald-300 bg-emerald-300/40" : "border-app-3"}`}
                            aria-hidden="true"
                        />
                        <span className="uppercase opacity-90">scan-to-remix qr</span>
                        <span className="opacity-50">·</span>
                        <span className="font-serif italic opacity-80">
                            {withQr ? "baked into download" : "make every share a loop"}
                        </span>
                    </button>
                {/* Inline pack iteration strip — primary post-generation
                    iteration surface. Users tap a pack tile to live-swap
                    the visual treatment and see exactly what each pack
                    looks like with their feeling. Moved here from Home
                    in iter-68 — iteration happens better with content
                    than empty form fields.
                    
                    `currentPack` is computed from the ACTIVE recipe (which
                    accounts for localStorage overrides) so when the user
                    iterates, the highlighted tile updates in lockstep. */}
                {(() => {
                    // eslint-disable-next-line no-unused-vars
                    const _t = variantTick;
                    const activeRecipe = isStylePackV2(mood?.pack || card?.pack)
                        ? resolveRecipe({
                            moodId: id,
                            pack: mood?.pack || card?.pack,
                            recipeId: card?.recipe_id,
                            customBackgroundUrl: mood?.custom_background_url || card?.custom_background_url,
                        })
                        : null;
                    return (
                            <PackIterationStrip
                                moodId={id}
                                currentPack={activeRecipe?.pack?.id || mood?.pack || card?.pack}
                                currentTone={mood?.personalize?.tone || null}
                                regenCount={mood?.regen_count || 0}
                                regenLimit={3}
                                isPremium={isPremium}
                                onChange={() => setVariantTick((n) => n + 1)}
                                onPremiumLocked={(packId) => setTeaserPackId(packId)}
                                onRegenerateTone={async (tone) => {
                                    try {
                                        const updated = await regenerateMoodCard(id, { tone });
                                        setMood(updated);
                                        toast.success(tone ? `rewrote in ${tone} voice` : "rewrote in auto voice");
                                        track("card_tone_regenerated", { mood_id: id, tone });
                                    } catch (e) {
                                        if (e?.response?.status === 402) {
                                            setPremiumOpen(true);
                                        } else {
                                            toast.error("couldn't regenerate — try again in a moment");
                                        }
                                        throw e;
                                    }
                                }}
                            />
                    );
                })()}

                {/* iter-72 (d): version history stepper — only renders when
                    the moodcard has prior versions (i.e. user has regenerated
                    at least once). Lets them step back to earlier wordings. */}
                <VersionStepper
                    moodId={id}
                    versions={mood?.versions}
                    onReverted={(updated) => {
                        setMood(updated);
                        toast.success("reverted to earlier wording");
                    }}
                />

                {/* "Look down" hint — sits between the card and the action
                    grid on mobile. Pulsing downward chevron tells new viewers
                    "the buttons are right here". Hidden on desktop where the
                    side column makes actions obvious. Auto-fades after 4s. */}
                <motion.div
                    className="lg:hidden flex flex-col items-center mt-2 mb-1 pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 4, times: [0, 0.18, 0.78, 1], ease: "easeInOut" }}
                    data-testid="card-actions-hint"
                >
                    <motion.div
                        animate={{ y: [0, 6, 0] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <ChevronDown className="w-5 h-5 text-amber-200/80" strokeWidth={1.6} />
                    </motion.div>
                    <span className="mt-1 font-sans text-[9px] uppercase tracking-[0.42em] text-amber-200/75">
                        save · share · keep
                    </span>
                </motion.div>

                <motion.div
                    className="grid grid-cols-3 gap-3"
                    initial={{ opacity: 0, y: 20, scale: 0.96 }}
                    animate={{ opacity: 1, y: [20, -4, 0], scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                >
                    {/* First-time tour hint — appears once when the user
                        lands on CardDetail. Hovers above the action row,
                        dismissed by ANY pointerdown anywhere on the page
                        OR auto after 6s. tourMarkSeen guarantees it
                        never re-appears. */}
                    <TourHint id="card-actions" show={true} anchor="below-card">
                        tap to share or save
                    </TourHint>
                    <button
                        onClick={handleDownload}
                        disabled={exporting}
                        data-testid="card-download-button"
                        className="btn-press flex flex-col items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 rounded-xl bg-app text-app-inverse font-sans text-xs font-medium tracking-wide hover:opacity-90 transition-opacity disabled:opacity-60"
                        style={{ backgroundColor: "var(--app-text)" }}
                    >
                        {downloaded ? <Check className="w-4 h-4" /> : <Download className="w-4 h-4" strokeWidth={1.6} />}
                        <span>{downloaded ? "Saved" : "Download"}</span>
                    </button>
                    <button
                        onClick={handleCopyCaption}
                        data-testid="card-copy-button"
                        className="btn-press flex flex-col items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 rounded-xl border border-app/30 bg-surface-1 text-app font-sans text-xs font-medium tracking-wide hover:bg-surface-2 transition-colors"
                    >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" strokeWidth={1.6} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </button>
                    <button
                        onClick={() => { tap(); track("card_shared", { id, style: card.style, pack: mood.pack }); setShareOpen(true); }}
                        data-testid="card-share-button"
                        className="btn-press flex flex-col items-center justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 rounded-xl border border-app/30 bg-surface-1 text-app font-sans text-xs font-medium tracking-wide hover:bg-surface-2 transition-colors"
                    >
                        <Share2 className="w-4 h-4" strokeWidth={1.6} />
                        <span>Share</span>
                    </button>
                    </motion.div>

                    {/* Restyle row — Restyle (full drawer) + Shuffle (one-tap
                        random recipe in the current pack). Shuffle is a
                        Premium feature; free users get a soft upsell toast.
                        Only shown for Style Pack v2 packs (legacy packs
                        lack named recipe variants). */}
                    <div className="mt-3 flex gap-2 items-stretch">
                        <button
                            onClick={() => { tap(); track("restyle_drawer_opened", { id }); setRestyleOpen(true); }}
                            data-testid="card-restyle-button"
                            className="btn-press flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-amber-300/25 bg-amber-400/[0.04] text-amber-200/85 hover:bg-amber-400/[0.08] hover:text-amber-100 font-sans text-xs uppercase tracking-[0.32em] transition-colors"
                        >
                            <Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            <span>restyle the mood</span>
                        </button>
                        {isStylePackV2(mood?.pack || card?.pack) && (
                            <button
                                onClick={() => {
                                    tap();
                                    const packId = mood?.pack || card?.pack;
                                    const currentRecipe = resolveRecipe({
                                        moodId: id,
                                        pack: packId,
                                        recipeId: card?.recipe_id,
                                        customBackgroundUrl: mood?.custom_background_url || card?.custom_background_url,
                                    });
                                    if (!isPremium) {
                                        track("shuffle_locked_clicked", { id, pack: packId });
                                        toast("Shuffle is a Premium feature — every other look unlocks with premium.", {
                                            action: { label: "See plans", onClick: () => navigate("/?premium=1") },
                                            duration: 5500,
                                        });
                                        return;
                                    }
                                    const next = pickShuffleRecipe(packId, currentRecipe?.variant?.id, { isPremium: true });
                                    if (!next) {
                                        toast("Only one look in this pack — open Restyle to swap packs.");
                                        return;
                                    }
                                    setRecipeOverride(id, next.key);
                                    track("shuffle_applied", { id, pack: packId, to: next.variant.id });
                                    setVariantTick((n) => n + 1);
                                    toast(`shuffled · ${next.variant.label.toLowerCase()}`, { duration: 2200 });
                                }}
                                data-testid="card-shuffle-button"
                                aria-label="Shuffle the look"
                                title={isPremium ? "Shuffle the look" : "Premium — shuffle within this pack"}
                                className="btn-press relative flex items-center justify-center w-12 rounded-xl border border-amber-300/25 bg-amber-400/[0.04] text-amber-200/85 hover:bg-amber-400/[0.10] hover:text-amber-100 transition-colors"
                            >
                                <Shuffle className="w-4 h-4" strokeWidth={1.6} />
                                {!isPremium && (
                                    <span className="absolute -top-1 -right-1 flex items-center justify-center h-4 w-4 rounded-full bg-stone-950 border border-amber-300/40">
                                        <Lock className="w-2.5 h-2.5 text-amber-200/85" strokeWidth={1.8} />
                                    </span>
                                )}
                            </button>
                        )}
                        {/* Motion toggle — applies recipe-attached animation
                            (drift/breathe/shimmer/glow) to the live preview.
                            Premium-only. Only renders when the active recipe
                            actually defines a motion treatment. */}
                        {(() => {
                            const packId = mood?.pack || card?.pack;
                            if (!isStylePackV2(packId)) return null;
                            const rcp = resolveRecipe({
                                moodId: id,
                                pack: packId,
                                recipeId: card?.recipe_id,
                                customBackgroundUrl: mood?.custom_background_url || card?.custom_background_url,
                            });
                            if (!rcp?.variant?.motion?.type) return null;
                            const handleMotionTap = () => {
                                tap();
                                if (!isPremium) {
                                    track("motion_locked_clicked", { id, pack: packId });
                                    toast("Motion is a Premium feature — let your card breathe.", {
                                        action: { label: "See plans", onClick: () => navigate("/?premium=1") },
                                        duration: 5500,
                                    });
                                    return;
                                }
                                const next = !motionOn;
                                setMotionOn(next);
                                try { window.localStorage.setItem(`moodcard.motion.${id}`, next ? "1" : "0"); } catch { /* ignore */ }
                                track(next ? "motion_enabled" : "motion_disabled", { id, type: rcp.variant.motion.type });
                                if (next) toast(`motion · ${rcp.variant.motion.type}`, { duration: 1800 });
                            };
                            return (
                                <button
                                    onClick={handleMotionTap}
                                    data-testid="card-motion-toggle"
                                    aria-label={motionOn ? "Pause motion" : "Play motion"}
                                    title={isPremium
                                        ? (motionOn ? "Pause card motion" : `Animate this card · ${rcp.variant.motion.type}`)
                                        : "Premium — let this card breathe"}
                                    className={`btn-press relative flex items-center justify-center w-12 rounded-xl border transition-colors ${
                                        motionOn && isPremium
                                            ? "border-amber-300/55 bg-amber-400/15 text-amber-100"
                                            : "border-amber-300/25 bg-amber-400/[0.04] text-amber-200/85 hover:bg-amber-400/[0.10] hover:text-amber-100"
                                    }`}
                                >
                                    {motionOn && isPremium
                                        ? <Pause className="w-4 h-4" strokeWidth={1.6} />
                                        : <Play className="w-4 h-4" strokeWidth={1.6} />}
                                    {!isPremium && (
                                        <span className="absolute -top-1 -right-1 flex items-center justify-center h-4 w-4 rounded-full bg-stone-950 border border-amber-300/40">
                                            <Lock className="w-2.5 h-2.5 text-amber-200/85" strokeWidth={1.8} />
                                        </span>
                                    )}
                                </button>
                            );
                        })()}
                    </div>

                    {/* Admin-only — pin to Editor's Picks on /explore. Hidden
                        from regular users. Posts to /api/admin/picks/{id}. */}
                    {isAdmin && (
                        <button
                            onClick={async () => {
                                try {
                                    await axios.post(`${API}/admin/picks/${id}`, {}, { withCredentials: true });
                                    toast.success("Pinned to Editor's Picks");
                                } catch (e) {
                                    toast.error("Pin failed: " + (e?.response?.data?.detail || e.message));
                                }
                            }}
                            data-testid="card-admin-pin-button"
                            className="btn-press mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl border border-app/15 text-app-3 hover:text-app hover:border-app/30 font-sans text-[10px] uppercase tracking-[0.32em] transition-colors"
                        >
                            ✦ pin to editor's picks
                        </button>
                    )}
                </motion.div>

                {/* premium teasers */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}
                    className="mt-10"
                >
                    <div className="flex items-center justify-between mb-3">
                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                            {isPremium ? "premium — unlocked" : "moodcard +"}
                        </span>
                        <span className="font-serif italic text-[11px] text-app-3">
                            {isPremium ? "✓" : "premium"}
                        </span>
                    </div>

                    {/* One-tap single-platform export — pick the size you want */}
                    <div className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Layers className="w-3.5 h-3.5 text-app-3" strokeWidth={1.5} />
                            <span className="font-sans text-[10px] uppercase tracking-[0.35em] text-app-3">
                                save for one platform
                            </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {[
                                { key: "story", label: "IG / TikTok",     sub: "9:16" },
                                { key: "feed",  label: "Feed",            sub: "1:1"  },
                                { key: "port",  label: "Portrait",        sub: "4:5"  },
                                { key: "x",     label: "X / FB",          sub: "16:9" },
                            ].map(({ key, label, sub }) => (
                                <button
                                    key={key}
                                    onClick={() => handleExportPlatform(key)}
                                    disabled={exporting}
                                    data-testid={`platform-export-${key}`}
                                    className={`flex flex-col items-center justify-center gap-1 py-3 rounded-xl border transition-colors ${
                                        isPremium
                                            ? "border-app/25 bg-surface-1 hover:bg-surface-2 hover:border-app/40"
                                            : "border-app/15 bg-surface-1 opacity-70"
                                    } disabled:opacity-40`}
                                >
                                    <span className="font-sans text-[11px] uppercase tracking-[0.18em] text-app">{label}</span>
                                    <span className="font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 flex items-center gap-1">
                                        {sub}
                                        {!isPremium && <Lock className="w-2.5 h-2.5" strokeWidth={1.6} />}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2.5">
                        {[
                            // iter-101 — "Animate this card" SHELVED for MVP
                            // (real video render not optimized; export hangs).
                            // Re-enable by uncommenting the entry below.
                            // { icon: Sparkles, label: "Animate this card",         hint: "real video", onClick: handleAnimate,         testid: "premium-animate" },
                            { icon: Eraser,   label: "Remove watermark",          hint: "clean export",   onClick: handleRemoveWatermark, testid: "premium-remove-watermark" },
                            { icon: Layers,   label: "Export every size at once", hint: "all platforms", onClick: handleAllPlatforms,    testid: "premium-all-platforms" },
                        ].map(({ icon: Icon, label, hint, onClick, testid }) => (
                            <button
                                key={label}
                                onClick={onClick}
                                data-testid={testid}
                                className="w-full flex items-center justify-between p-4 rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 hover:border-app/40 transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <Icon className="w-4 h-4 text-app-2 group-hover:text-app transition-colors" strokeWidth={1.5} />
                                    <span className="font-sans text-sm text-app">{label}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                        {hint}
                                    </span>
                                    {!isPremium && <Lock className="w-3 h-3 text-app-3" strokeWidth={1.5} />}
                                </div>
                            </button>
                        ))}
                    </div>

                    {!isPremium && (
                        <button
                            onClick={() => { track("watermark_upgrade_clicked"); setPremiumOpen(true); }}
                            data-testid="unlock-premium-button"
                            className="mt-5 w-full py-3.5 rounded-full bg-app text-app-inverse font-sans text-sm font-medium tracking-wide hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: "var(--app-text)" }}
                        >
                            Remove watermark — from $7.99/mo
                        </button>
                    )}
                </motion.div>
                </div>{/* /legacy lg:block desktop UI wrapper (iter-73) */}
                </div>{/* /right column */}
                </div>{/* /lg:grid */}
            </div>

            <RestyleDrawer
                open={restyleOpen}
                onClose={() => setRestyleOpen(false)}
                moodId={id}
                pack={mood?.pack || card?.pack}
                customBackgroundUrl={mood?.custom_background_url || card?.custom_background_url}
                currentVariantId={
                    isStylePackV2(mood?.pack || card?.pack)
                        ? (resolveRecipe({
                            moodId: id,
                            pack: mood?.pack || card?.pack,
                            recipeId: card?.recipe_id,
                            customBackgroundUrl: mood?.custom_background_url || card?.custom_background_url,
                        })?.variant?.id || null)
                        : resolveVariant(id).id
                }
                onVariantChange={() => setVariantTick((n) => n + 1)}
                onCustomBackgroundChange={() => {
                    // Refetch the mood so card.custom_background_url updates.
                    setVariantTick((n) => n + 1);
                    getMoodCard(id).then((m) => setMood(m)).catch(() => {});
                }}
                thumbnailBg={card?.background_url ? `url(${card.background_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${card.background_url}` : card.background_url})` : null}
                isPremium={!!isPremium}
                onPremiumLocked={() => {
                    setRestyleOpen(false);
                    toast("Restyle is a Premium feature — unlock every visual recipe + every pack.", {
                        action: { label: "See plans", onClick: () => navigate("/?premium=1") },
                        duration: 6000,
                    });
                }}
            />

            <ShareSheet
                open={shareOpen}
                onOpenChange={(v) => { setShareOpen(v); if (!v) setTimeout(() => setShareAfterOpen(true), 400); }}
                caption={buildShareCaption({ card, shareUrl: canonShareUrl })}
                captionNoUrl={buildShareCaptionText({ card })}
                shareUrl={canonShareUrl}
                exportPng={async () => await exportNode(exportRefStory.current)}
                onCopyCaption={handleCopyCaption}
                onDownload={handleDownload}
                privateMessage={buildPrivateShareMessage({ card, mood, shareUrl: canonShareUrl }).text}
                privateCapsule={buildPrivateShareMessage({ card, mood, shareUrl: canonShareUrl }).capsule}
                privateTone={buildPrivateShareMessage({ card, mood, shareUrl: canonShareUrl }).tone}
                defaultRecipient={replyToDefault}
            />
            <ShareAfter
                open={shareAfterOpen}
                onOpenChange={setShareAfterOpen}
                shareUrl={canonShareUrl}
                caption={buildShareCaption({ card, shareUrl: canonShareUrl })}
            />
            <PremiumModal open={premiumOpen} onOpenChange={setPremiumOpen} source="card_detail" />
            {/* iter-72 (b): friendly intermediate teaser before full pricing modal */}
            <PremiumPackTeaser
                open={!!teaserPackId}
                packId={teaserPackId}
                onClose={() => setTeaserPackId(null)}
                onSeeAll={() => { setTeaserPackId(null); setPremiumOpen(true); }}
            />
            <AnimatedExport open={animateOpen} onOpenChange={setAnimateOpen} card={card} />

            {/* Download progress overlay — scanner sweep + status ticker.
                Shows while `exporting` is true. Renders a small clone of
                the card inside so the user sees WHAT is being rendered.
                Auto-dismisses on download completion (handleDownload
                always sets exporting=false in finally). */}
            <DownloadOverlay open={exporting}>
                <MoodCard
                    card={card}
                    moodId={id}
                    showWatermark={showWatermark}
                    lane={mood.lane || mood.personalize?.lane}
                    temperature={mood.personalize?.temperature || mood.temperature}
                    variant={resolveVariant(id)}
                />
            </DownloadOverlay>

            {/* Lightbox — full-screen enlarged preview of the card. Tap card
                to open; tap backdrop or X to dismiss. Lets users read at
                comfortable size without having to download. */}
            <AnimatePresence>
                {lightboxOpen && (
                    <motion.div
                        key="lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        onClick={() => setLightboxOpen(false)}
                        className="fixed inset-0 z-[130] flex items-center justify-center px-6"
                        style={{
                            background: "rgba(8,9,14,0.88)",
                            backdropFilter: "blur(18px)",
                            WebkitBackdropFilter: "blur(18px)",
                        }}
                        data-testid="card-lightbox"
                    >
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                            data-testid="card-lightbox-close"
                            aria-label="close preview"
                            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 transition-colors flex items-center justify-center"
                        >
                            <X className="w-4 h-4 text-app" strokeWidth={1.8} />
                        </button>
                        <motion.div
                            initial={{ scale: 0.94, y: 14 }}
                            animate={{ scale: 1, y: 0 }}
                            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-[340px] sm:max-w-[420px] rounded-2xl overflow-hidden shadow-2xl"
                        >
                            <MoodCard
                                card={card}
                                moodId={id}
                                showWatermark={showWatermark}
                                lane={mood.lane || mood.personalize?.lane}
                                temperature={mood.personalize?.temperature || mood.temperature}
                                variant={resolveVariant(id)}
                            />
                        </motion.div>
                        <p className="absolute bottom-6 left-0 right-0 text-center font-sans text-[10px] uppercase tracking-[0.42em] text-app-3">
                            tap anywhere to close
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* iter-73: mobile action bar + studio sheets. Hidden on lg+ via
                the container itself; sheets are no-ops when their `open` flag
                is false so they're safe to mount unconditionally. */}
            <MobileActionBar
                onRemix={() => { track("mobile_action_remix"); setRemixOpen(true); }}
                onAnimate={() => {
                    track("mobile_action_animate");
                    if (isPremium) {
                        setAnimateOpen(true);
                    } else {
                        setAnimateTeaserOpen(true);
                    }
                }}
                onShare={() => { track("mobile_action_share"); tap(); setShareOpen(true); }}
                onDownload={() => { track("mobile_action_download"); setDownloadSheetOpen(true); }}
                isPremium={isPremium}
            />
            {/* iter-73: spacer so bottom action bar doesn't cover the scrolling
                content. h-32 + safe-area padding clears ~80px action bar +
                pb-3 outer padding + iPhone notch / home-indicator zones. */}
            <div
                className="lg:hidden"
                style={{ height: "calc(7rem + env(safe-area-inset-bottom))" }}
                aria-hidden="true"
            />

            <RemixStudio
                open={remixOpen}
                onClose={() => setRemixOpen(false)}
                moodId={id}
                currentPack={mood?.pack || card?.pack}
                currentTone={mood?.personalize?.tone || null}
                versions={mood?.versions}
                isPremium={isPremium}
                onRegenerateTone={async (tone) => {
                    try {
                        const updated = await regenerateMoodCard(id, { tone });
                        setMood(updated);
                        toast.success(tone ? `rewrote in ${tone} voice` : "rewrote in auto voice");
                        track("card_tone_regenerated", { mood_id: id, tone });
                    } catch (e) {
                        if (e?.response?.status === 402) {
                            setPremiumOpen(true);
                        } else {
                            toast.error("couldn't regenerate — try again in a moment");
                        }
                        throw e;
                    }
                }}
                onPackChange={async (packId) => {
                    setVariantTick((n) => n + 1);
                    // iter-73 fix: persist pack swap from RemixStudio too.
                    try {
                        const updated = await switchPack(id, packId);
                        setMood(updated);
                    } catch (e) {
                        if (e?.response?.status === 402) {
                            setRemixOpen(false);
                            setTeaserPackId(packId);
                        }
                    }
                }}
                onPremiumLocked={(packId) => { setRemixOpen(false); setTeaserPackId(packId); }}
                onReverted={(updated) => {
                    setMood(updated);
                    toast.success("reverted to earlier wording");
                }}
            />

            <DownloadSheet
                open={downloadSheetOpen}
                onClose={() => setDownloadSheetOpen(false)}
                exporting={exporting}
                onPick={(platformKey) => {
                    setDownloadSheetOpen(false);
                    // Map our new platform keys onto the existing handleExportPlatform(key)
                    // signature. "tiktok" and "hd" reuse "story" — same 9:16 1080×1920 export.
                    const k =
                        platformKey === "tiktok" || platformKey === "hd"
                            ? "story"
                            : platformKey;
                    handleExportPlatform(k);
                }}
            />

            <AnimateTeaser
                open={animateTeaserOpen}
                onClose={() => setAnimateTeaserOpen(false)}
                onUnlock={() => { setAnimateTeaserOpen(false); setPremiumOpen(true); }}
            />
        </div>
    );
}
